import React, { useEffect } from 'react';
import HelpLayout from '../../../components/help/HelpLayout';

const DjangoAdminHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'Admin Guide', href: '/help/admin/dashboard' },
    { name: 'Django Admin' }
  ];

  return (
    <HelpLayout title="Django Admin Interface" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        <div className="card">
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Django Admin Interface Guide
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6
            }}>
              Advanced administration through the Django admin panel including data management and bulk operations.
            </p>

            {/* Access and Navigation */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                🚪 Accessing Django Admin
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Admin Panel Access
                </h4>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '6px',
                  border: '1px solid #f59e0b',
                  marginBottom: '16px'
                }}>
                  <h5 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#d97706',
                    marginBottom: '8px'
                  }}>
                    🔐 Access Requirements
                  </h5>
                  <ul style={{ fontSize: '14px', color: '#d97706', margin: 0, paddingLeft: '16px' }}>
                    <li>Staff status must be enabled on your user account</li>
                    <li>Superuser status for full administrative access</li>
                    <li>Specific permissions for limited administrative roles</li>
                  </ul>
                </div>
                <ol style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li>Navigate to <strong>http://localhost:8000/admin/</strong> (or your domain + /admin/)</li>
                  <li>Enter your administrator username and password</li>
                  <li>Click "Log in" to access the admin dashboard</li>
                  <li>Use the navigation menu to access different administrative sections</li>
                </ol>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Admin Dashboard Overview
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  The Django admin dashboard provides organized access to all system data:
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      👥 Users Section
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '16px' }}>
                      <li>User accounts and profiles</li>
                      <li>Education records</li>
                      <li>Matric subjects</li>
                    </ul>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      🎓 Courses Section
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '16px' }}>
                      <li>Course management</li>
                      <li>Course intakes</li>
                      <li>Categories and campuses</li>
                    </ul>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      📝 Registrations Section
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '16px' }}>
                      <li>Student applications</li>
                      <li>Registration status</li>
                      <li>Wishlist management</li>
                    </ul>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      📢 Notifications Section
                    </h5>
                    <ul style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, paddingLeft: '16px' }}>
                      <li>System announcements</li>
                      <li>User notifications</li>
                      <li>Notification history</li>
                    </ul>
                  </div>
                </div>
              </div>
            {/* Data Management */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                📊 Data Management Operations
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  List View Operations
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  The admin list views provide powerful tools for managing large datasets:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>Filtering:</strong> Use right sidebar filters to narrow down records</li>
                  <li><strong>Search:</strong> Search across multiple fields using the search box</li>
                  <li><strong>Sorting:</strong> Click column headers to sort by different criteria</li>
                  <li><strong>Pagination:</strong> Navigate through large datasets efficiently</li>
                  <li><strong>Bulk Selection:</strong> Select multiple records for bulk operations</li>
                </ul>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Bulk Operations
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '6px',
                    border: '1px solid #16a34a'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#16a34a',
                      marginBottom: '8px'
                    }}>
                      ✅ Status Updates
                    </h5>
                    <p style={{ fontSize: '14px', color: '#16a34a', margin: 0 }}>
                      Update application status for multiple students simultaneously
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '6px',
                    border: '1px solid #0284c7'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0284c7',
                      marginBottom: '8px'
                    }}>
                      📧 Bulk Notifications
                    </h5>
                    <p style={{ fontSize: '14px', color: '#0284c7', margin: 0 }}>
                      Send announcements to selected user groups
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    border: '1px solid #f59e0b'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#d97706',
                      marginBottom: '8px'
                    }}>
                      📊 Data Export
                    </h5>
                    <p style={{ fontSize: '14px', color: '#d97706', margin: 0 }}>
                      Export selected records to CSV or other formats
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '6px',
                    border: '1px solid #ef4444'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#dc2626',
                      marginBottom: '8px'
                    }}>
                      🗑️ Bulk Deletion
                    </h5>
                    <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>
                      Delete multiple records (use with extreme caution)
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Advanced Filtering
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Use sophisticated filters to find specific records:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>Date Filters:</strong> Filter by creation date, modification date, or custom date fields</li>
                  <li><strong>Status Filters:</strong> Filter applications by status (applied, accepted, registered, etc.)</li>
                  <li><strong>User Type Filters:</strong> Filter by staff status, active status, or user permissions</li>
                  <li><strong>Course Filters:</strong> Filter by course category, campus, or NQF level</li>
                  <li><strong>Custom Filters:</strong> Use specific field values for precise filtering</li>
                </ul>
              </div>
            </div>

            {/* Security and Permissions */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                🔒 Security and Permissions
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Permission Levels
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '6px',
                    borderLeft: '4px solid #16a34a'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#16a34a',
                      marginBottom: '4px'
                    }}>
                      🛡️ Superuser Access
                    </h5>
                    <p style={{ fontSize: '14px', color: '#16a34a', margin: 0 }}>
                      Complete access to all models, data, and administrative functions. Use sparingly.
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    borderLeft: '4px solid #f59e0b'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#d97706',
                      marginBottom: '4px'
                    }}>
                      👨‍💼 Staff Access
                    </h5>
                    <p style={{ fontSize: '14px', color: '#d97706', margin: 0 }}>
                      Limited access to specific models based on assigned permissions.
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '6px',
                    borderLeft: '4px solid #0284c7'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0284c7',
                      marginBottom: '4px'
                    }}>
                      📝 Model Permissions
                    </h5>
                    <p style={{ fontSize: '14px', color: '#0284c7', margin: 0 }}>
                      Granular permissions: add, change, delete, and view for each model.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Security Best Practices
                </h4>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fee2e2',
                  borderRadius: '6px',
                  border: '1px solid #ef4444',
                  marginBottom: '16px'
                }}>
                  <h5 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#dc2626',
                    marginBottom: '8px'
                  }}>
                    ⚠️ Critical Security Guidelines
                  </h5>
                  <ul style={{ fontSize: '14px', color: '#dc2626', margin: 0, paddingLeft: '16px' }}>
                    <li>Never share admin credentials with unauthorized personnel</li>
                    <li>Use strong, unique passwords for admin accounts</li>
                    <li>Log out of admin sessions when finished</li>
                    <li>Monitor admin access logs regularly</li>
                    <li>Grant minimum necessary permissions to staff users</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Best Practices */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--background-gray)',
              borderRadius: '8px',
              marginTop: '32px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                💡 Django Admin Best Practices
              </h3>
              <ul style={{
                color: 'var(--text-gray)',
                lineHeight: 1.6,
                paddingLeft: '20px',
                margin: 0
              }}>
                <li><strong>Regular backups:</strong> Backup data before making bulk changes</li>
                <li><strong>Test in staging:</strong> Test bulk operations in a non-production environment first</li>
                <li><strong>Use filters effectively:</strong> Always filter data before bulk operations</li>
                <li><strong>Document changes:</strong> Keep records of significant administrative actions</li>
                <li><strong>Monitor performance:</strong> Large bulk operations can impact system performance</li>
                <li><strong>Verify permissions:</strong> Ensure users have appropriate access levels</li>
                <li><strong>Regular audits:</strong> Review admin access and permissions quarterly</li>
                <li><strong>Data validation:</strong> Verify data integrity after bulk operations</li>
                <li><strong>User training:</strong> Ensure admin users understand the interface and procedures</li>
                <li><strong>Emergency procedures:</strong> Have rollback plans for critical operations</li>
              </ul>
            </div>

            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default DjangoAdminHelp;
